// Report Builder period navigation (plan 1.6) — a thin switch on a template's
// timeframe over four pill components sharing the .week-nav-pill chrome:
//   weekly  → the existing shared WeekNavPill (unchanged),
//   daily   → DayNavPill   (±1 day chevrons + Today),
//   monthly → MonthNavPill (±1 month chevrons + Today, "Jul 2026" label),
//   custom  → CustomRangePill (two date inputs — extracted from the Posts
//             toolbar's inline JSX; PostsGrid consumes the extraction too).
// Unlike the Posts toolbar there is no Today/Week/Custom segment here: the
// Reports screen's template dropdown dictates the timeframe, so only the
// matching pill renders.

import React from 'react';
import { WeekNavPill } from './WeekNavigator';
import { ReportTimeframe } from '../../models/reportTemplate';
import { getMonday, toDateString, pad } from '../../services/dateUtils';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const parseLocal = (dateStr: string): Date => new Date(dateStr + 'T00:00');

interface DayNavPillProps {
  date: string; // YYYY-MM-DD
  onDayChange: (date: string) => void;
  allowFuture?: boolean;
}

/** [◀  Wed 16 Jul 2026  ▶  Today] — the daily counterpart of WeekNavPill. */
export const DayNavPill: React.FC<DayNavPillProps> = ({ date, onDayChange, allowFuture = false }) => {
  const today = toDateString(new Date());
  const blockNext = !allowFuture && date >= today;
  const showTodayButton = date !== today;

  const navigate = (direction: -1 | 1) => {
    const d = parseLocal(date);
    d.setDate(d.getDate() + direction);
    onDayChange(toDateString(d));
  };

  const d = parseLocal(date);
  const label = `${WEEKDAY_SHORT[d.getDay()]} ${pad(d.getDate())} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;

  return (
    <div className="week-nav-pill" role="group" aria-label="Day navigation" data-help="period-nav">
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => navigate(-1)}
        title="Previous day"
        aria-label="Previous day"
      >
        <i className="fas fa-chevron-left" />
      </button>
      <span className="week-nav-pill__label">{label}</span>
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => navigate(1)}
        disabled={blockNext}
        title={blockNext ? 'Already on today' : 'Next day'}
        aria-label="Next day"
      >
        <i className="fas fa-chevron-right" />
      </button>
      {showTodayButton && (
        <button
          type="button"
          className="week-nav-pill__today"
          onClick={() => onDayChange(today)}
          title="Jump to today"
        >
          <i className="fas fa-calendar-day" /> Today
        </button>
      )}
    </div>
  );
};

interface MonthNavPillProps {
  /** Any date inside the month, YYYY-MM-DD (callers conventionally pass the 1st). */
  monthStart: string;
  onMonthChange: (firstOfMonth: string, lastOfMonth: string) => void;
  allowFuture?: boolean;
}

/** [◀  Jul 2026  ▶  Today] — WeekNavPill cloned with a 1-month step. */
export const MonthNavPill: React.FC<MonthNavPillProps> = ({ monthStart, onMonthChange, allowFuture = false }) => {
  const base = parseLocal(monthStart);
  const now = new Date();
  const sameMonthAsToday = base.getFullYear() === now.getFullYear() && base.getMonth() === now.getMonth();
  const blockNext = !allowFuture &&
    (base.getFullYear() > now.getFullYear() ||
      (base.getFullYear() === now.getFullYear() && base.getMonth() >= now.getMonth()));

  const emitMonth = (year: number, month: number) => {
    onMonthChange(
      toDateString(new Date(year, month, 1)),
      toDateString(new Date(year, month + 1, 0))
    );
  };

  const label = `${MONTH_SHORT[base.getMonth()]} ${base.getFullYear()}`;

  return (
    <div className="week-nav-pill" role="group" aria-label="Month navigation" data-help="period-nav">
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => emitMonth(base.getFullYear(), base.getMonth() - 1)}
        title="Previous month"
        aria-label="Previous month"
      >
        <i className="fas fa-chevron-left" />
      </button>
      <span className="week-nav-pill__label">{label}</span>
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => emitMonth(base.getFullYear(), base.getMonth() + 1)}
        disabled={blockNext}
        title={blockNext ? 'Already on current month' : 'Next month'}
        aria-label="Next month"
      >
        <i className="fas fa-chevron-right" />
      </button>
      {!sameMonthAsToday && (
        <button
          type="button"
          className="week-nav-pill__today"
          onClick={() => emitMonth(now.getFullYear(), now.getMonth())}
          title="Jump to current month"
        >
          <i className="fas fa-calendar-day" /> Today
        </button>
      )}
    </div>
  );
};

interface CustomRangePillProps {
  fromDate: string | null;
  toDate: string | null;
  onFromChange: (v: string | null) => void;
  onToChange: (v: string | null) => void;
  /** Adds the --inline modifier (margin) for use beside a toolbar segment. */
  inline?: boolean;
}

/** Two date inputs in the shared pill chrome — extracted from the Posts
 *  toolbar's Custom mode so Reports and Posts render the identical control. */
export const CustomRangePill: React.FC<CustomRangePillProps> = ({
  fromDate, toDate, onFromChange, onToChange, inline = false,
}) => (
  <div
    className={`week-nav-pill${inline ? ' week-nav-pill--inline' : ''}`}
    style={{ flexShrink: 0 }}
    role="group"
    aria-label="Custom date range"
    data-help="period-nav"
  >
    <input
      type="date"
      value={fromDate || ''}
      onChange={(e) => onFromChange(e.target.value || null)}
      className="week-nav-pill__date-input"
      aria-label="From date"
    />
    <span className="week-nav-pill__sep" aria-hidden="true">to</span>
    <input
      type="date"
      value={toDate || ''}
      onChange={(e) => onToChange(e.target.value || null)}
      className="week-nav-pill__date-input"
      aria-label="To date"
    />
  </div>
);

export interface PeriodNavigatorProps {
  timeframe: ReportTimeframe;
  /** Current period bounds, YYYY-MM-DD. Null falls back to the current period. */
  startDate: string | null;
  endDate: string | null;
  /** onWeekChange-shaped: weekly/daily/monthly always emit both bounds;
   *  custom passes through whatever the inputs hold (possibly null). */
  onPeriodChange: (startDate: string | null, endDate: string | null) => void;
  allowFuture?: boolean;
}

/**
 * Renders the period control matching a report template's timeframe.
 * Weekly is the untouched shared WeekNavPill (Mon–Fri, as everywhere else).
 */
const PeriodNavigator: React.FC<PeriodNavigatorProps> = ({
  timeframe, startDate, endDate, onPeriodChange, allowFuture,
}) => {
  switch (timeframe) {
    case 'weekly': {
      const weekStart = startDate ?? toDateString(getMonday(new Date()));
      return (
        <WeekNavPill
          weekStart={weekStart}
          onWeekChange={onPeriodChange}
          allowFuture={allowFuture}
        />
      );
    }
    case 'daily': {
      const date = startDate ?? toDateString(new Date());
      return (
        <DayNavPill
          date={date}
          onDayChange={(d) => onPeriodChange(d, d)}
          allowFuture={allowFuture}
        />
      );
    }
    case 'monthly': {
      const now = new Date();
      const monthStart = startDate ?? toDateString(new Date(now.getFullYear(), now.getMonth(), 1));
      return (
        <MonthNavPill
          monthStart={monthStart}
          onMonthChange={onPeriodChange}
          allowFuture={allowFuture}
        />
      );
    }
    case 'custom':
      return (
        <CustomRangePill
          fromDate={startDate}
          toDate={endDate}
          onFromChange={(v) => onPeriodChange(v, endDate)}
          onToChange={(v) => onPeriodChange(startDate, v)}
        />
      );
    default:
      return null;
  }
};

export default PeriodNavigator;
